import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface MetaRow {
  readonly metadataId: string;
  readonly objectType: string;
  readonly objectName: string;
  readonly description: string | null;
}

/** M26 — Metadata Repository (§19). Central dataset/field/glossary catalogue. */
export function M26MetadataRepositoryPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<MetaRow[]>([]);
  const [total, setTotal] = useState(0);
  const [objectType, setObjectType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const q = objectType ? `?objectType=${encodeURIComponent(objectType)}` : '';
    apiRequest<{ data: MetaRow[]; total: number }>(`/v1/dwh/governance/metadata${q}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, objectType]);

  return (
    <main aria-label="metadata-repository">
      <h1>{tr('m26.metadata.title')}</h1>
      <label htmlFor="meta-filter">{tr('m26.metadata.objectTypeLabel')}</label>
      <select id="meta-filter" value={objectType} onChange={(e) => setObjectType(e.target.value)}>
        {['', 'DATASET', 'FIELD', 'GLOSSARY', 'SOURCE', 'CALCULATION'].map((t) => <option key={t || 'all'} value={t}>{t || tr('m26.metadata.all')}</option>)}
      </select>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m26.metadata.loading')}</p> : (
        <>
          <p aria-label="meta-count">{tr('m26.metadata.count', { count: total })}</p>
          <table aria-label="meta-table">
            <thead><tr><th>{tr('m26.metadata.colType')}</th><th>{tr('m26.metadata.colName')}</th><th>{tr('m26.metadata.colDescription')}</th></tr></thead>
            <tbody>{rows.map((m) => <tr key={m.metadataId}><td>{m.objectType}</td><td>{m.objectName}</td><td>{m.description ?? '—'}</td></tr>)}</tbody>
          </table>
        </>
      )}
    </main>
  );
}
